/**
 * SearchableCombobox - Single-select dropdown with type-to-filter behaviour.
 *
 * Wraps Fluent v9 `Combobox` and adds the filter-as-you-type pattern most
 * pickers in this app need: typing into the input is reflected in the field
 * and filters the visible options (case-insensitive match against `text`,
 * `secondaryText`, `groupLabel`, and any extra `searchTerms`). When the
 * popup closes or an option is picked, the input snaps back to the
 * selected option's display text.
 *
 * Use this for any list-of-known-values picker — subscriptions, projects,
 * connections, timezones, etc. For pickers that also need to accept a
 * typed custom value or an expression token, use `ComboboxField` instead;
 * it builds on the same idea but adds token / expression support.
 */
import { useCallback, useMemo, useState, isValidElement, type ReactNode } from 'react';
import {
  Combobox,
  Option,
  OptionGroup,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
  type ComboboxProps,
  type OptionOnSelectData,
  type SelectionEvents,
} from '@fluentui/react-components';
import { comboboxListboxStyles } from '@/styles/comboboxListbox';

export interface SearchableComboboxOption {
  /** Stable identifier returned via `onChange` and used as React key. */
  value: string;
  /** Primary label — shown in the trigger when selected and matched against search. */
  text: string;
  /** Optional secondary label rendered below the primary text in the dropdown. Also matched against search. */
  secondaryText?: string;
  /**
   * Optional extra strings to match against when filtering. Use this to make
   * hidden identifiers (full GUIDs, ARM resource IDs, etc.) searchable.
   * The bare `value` is NOT included in the default filter, since opaque
   * IDs often contain misleading substrings.
   */
  searchTerms?: readonly string[];
  /**
   * Optional group label. Options that share a label render under the same
   * Fluent `OptionGroup`. Also included in the default search filter so
   * users can type the visible group header to narrow down.
   */
  groupLabel?: string;
  /**
   * Optional grouping key. Defaults to `groupLabel`. Use this when two
   * groups can share the same human-readable label (e.g., two subscriptions
   * with the same display name) and you need stable grouping by ID.
   */
  groupKey?: string;
  /** Disable this option. */
  disabled?: boolean;
}

export interface SearchableComboboxProps {
  options: readonly SearchableComboboxOption[];
  /** Currently selected option `value`, or `undefined` when nothing is selected. */
  value: string | undefined;
  /** Called when the user picks an option from the list. */
  onChange: (value: string) => void;

  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: ComboboxProps['size'];

  /**
   * Display text used when `value` is set but no matching option exists
   * (e.g., the option list is still loading, or the value references a
   * deleted resource). Defaults to the raw `value` so the user still sees
   * what's selected rather than a blank field.
   */
  fallbackDisplayValue?: string;

  /** Accessible name. Required when the component isn't wrapped in a labelled `Field`. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;

  /**
   * Custom filter predicate. Receives the option and the lower-cased trimmed
   * search term. Defaults to a case-insensitive substring match against
   * `text`, `secondaryText`, `groupLabel`, and `searchTerms`.
   */
  filterOption?: (option: SearchableComboboxOption, searchTerm: string) => boolean;
  /** Message rendered as a disabled option when the search yields no matches. */
  noMatchMessage?: string;

  /**
   * Custom renderer for an option's contents. Defaults to a two-line layout
   * with `text` on top and `secondaryText` (when present) muted below.
   */
  renderOption?: (option: SearchableComboboxOption) => ReactNode;

  /** Forwarded to the underlying Fluent `Combobox` for advanced positioning needs. */
  positioning?: ComboboxProps['positioning'];
  /** Forwarded to the underlying Fluent `Combobox` (e.g., custom listbox className). */
  listbox?: ComboboxProps['listbox'];
  /** Forwarded to Fluent `Combobox`. Useful inside dialogs that own the focus trap. */
  inlinePopup?: boolean;
}

const NO_MATCH_VALUE = '__searchable-combobox-no-match__';

const useStyles = makeStyles({
  twoLineOption: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  primaryLine: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondaryLine: {
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Cap the dropdown so long option lists scroll inside the popup instead of
  // stretching to fill the viewport. Shares `comboboxListboxStyles` with the
  // rest of the app so every combobox listbox has the same cap + thin
  // scrollbar. Callers can override by passing their own `listbox.className`.
  listbox: comboboxListboxStyles,
});

/** Default option filter: case-insensitive substring match across visible/searchable fields. */
const defaultFilter = (option: SearchableComboboxOption, searchTerm: string): boolean => {
  if (option.text.toLowerCase().includes(searchTerm)) return true;
  if (option.secondaryText?.toLowerCase().includes(searchTerm)) return true;
  if (option.groupLabel?.toLowerCase().includes(searchTerm)) return true;
  if (option.searchTerms) {
    for (const term of option.searchTerms) {
      if (term.toLowerCase().includes(searchTerm)) return true;
    }
  }
  return false;
};

export const SearchableCombobox = ({
  options,
  value,
  onChange,
  id,
  className,
  placeholder,
  disabled,
  fallbackDisplayValue,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  filterOption = defaultFilter,
  noMatchMessage = 'No matches found',
  renderOption,
  positioning,
  listbox,
  inlinePopup,
  size,
}: SearchableComboboxProps) => {
  const styles = useStyles();

  // `null` means the user isn't actively typing — display the selected
  // option's text instead. A string (including '') means they've typed, and
  // we honour that exactly so backspacing to empty doesn't snap back.
  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  const selectedText = useMemo(() => {
    if (value === undefined) return '';
    const matched = options.find((opt) => opt.value === value);
    if (matched) return matched.text;
    // Value exists but isn't in the options list (still loading, deleted,
    // or out-of-scope) — fall back so users still see something selected.
    return fallbackDisplayValue ?? value;
  }, [options, value, fallbackDisplayValue]);

  const filteredOptions = useMemo(() => {
    if (searchTerm === null) return options;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => filterOption(opt, term));
  }, [options, searchTerm, filterOption]);

  const renderOptionContent = useCallback(
    (opt: SearchableComboboxOption): ReactNode => {
      if (renderOption) return renderOption(opt);
      if (!opt.secondaryText) return opt.text;
      return (
        <div className={styles.twoLineOption}>
          <Text weight="semibold" className={styles.primaryLine}>
            {opt.text}
          </Text>
          <Text size={200} className={styles.secondaryLine}>
            {opt.secondaryText}
          </Text>
        </div>
      );
    },
    [renderOption, styles],
  );

  const handleOptionSelect = useCallback(
    (_: SelectionEvents, data: OptionOnSelectData) => {
      const next = data.optionValue;
      if (next === undefined || next === NO_MATCH_VALUE) return;
      onChange(next);
      setSearchTerm(null);
    },
    [onChange],
  );

  const handleInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleInputBlur = useCallback(() => {
    setSearchTerm(null);
  }, []);

  const handleOpenChange = useCallback<NonNullable<ComboboxProps['onOpenChange']>>((_, data) => {
    if (!data.open) setSearchTerm(null);
  }, []);

  // Build the rendered children. We render groups only when at least one
  // visible option declares a `groupLabel`; otherwise we render a flat list
  // so callers without grouping don't pay for the wrapper.
  const renderedOptions = useMemo(() => {
    if (filteredOptions.length === 0) {
      return (
        <Option key="__no-match__" value={NO_MATCH_VALUE} disabled text={noMatchMessage}>
          {noMatchMessage}
        </Option>
      );
    }
    const hasGroups = filteredOptions.some((opt) => opt.groupLabel);
    if (!hasGroups) {
      return filteredOptions.map((opt) => (
        <Option key={opt.value} value={opt.value} text={opt.text} disabled={opt.disabled ?? false}>
          {renderOptionContent(opt)}
        </Option>
      ));
    }
    // Group preserving insertion order, keyed by `groupKey ?? groupLabel`
    // so two groups that happen to share a display name still stay separate.
    type Group = { key: string; label: string; items: SearchableComboboxOption[] };
    const groups: Group[] = [];
    const indexByKey = new Map<string, number>();
    for (const opt of filteredOptions) {
      const key = opt.groupKey ?? opt.groupLabel ?? '__ungrouped__';
      let idx = indexByKey.get(key);
      if (idx === undefined) {
        idx = groups.length;
        indexByKey.set(key, idx);
        groups.push({ key, label: opt.groupLabel ?? '', items: [] });
      }
      groups[idx]!.items.push(opt);
    }
    return groups.flatMap((group) => {
      const items = group.items.map((opt) => (
        <Option key={opt.value} value={opt.value} text={opt.text} disabled={opt.disabled ?? false}>
          {renderOptionContent(opt)}
        </Option>
      ));
      if (!group.label) return items;
      return [
        <OptionGroup key={`group-${group.key}`} label={group.label}>
          {items}
        </OptionGroup>,
      ];
    });
  }, [filteredOptions, noMatchMessage, renderOptionContent]);

  const displayValue = searchTerm ?? selectedText;
  const selectedOptions = useMemo(() => (value === undefined ? [] : [value]), [value]);

  // Merge our default listbox className (max-height + scroll) with whatever
  // the caller passed. Caller's className wins via mergeClasses precedence,
  // but any other slot props they set (style, ref, etc.) are preserved.
  const mergedListbox = useMemo(() => {
    if (listbox === undefined) {
      return { className: styles.listbox };
    }
    // Caller passed a React element — respect it as-is.
    if (isValidElement(listbox)) return listbox;
    if (typeof listbox === 'object' && listbox !== null) {
      const lb = listbox as { className?: string };
      return { ...lb, className: mergeClasses(styles.listbox, lb.className) };
    }
    return listbox;
  }, [listbox, styles.listbox]);

  // With exactOptionalPropertyTypes, optional slot props must be omitted
  // entirely when undefined rather than passed as `undefined`. Memoize so
  // the spread doesn't hand a fresh object to Combobox on every render.
  const optionalProps = useMemo(() => {
    const props: Record<string, unknown> = { listbox: mergedListbox };
    if (id !== undefined) props.id = id;
    if (className !== undefined) props.className = className;
    if (placeholder !== undefined) props.placeholder = placeholder;
    if (disabled !== undefined) props.disabled = disabled;
    if (ariaLabel !== undefined) props['aria-label'] = ariaLabel;
    if (ariaLabelledBy !== undefined) props['aria-labelledby'] = ariaLabelledBy;
    if (ariaDescribedBy !== undefined) props['aria-describedby'] = ariaDescribedBy;
    if (ariaInvalid !== undefined) props['aria-invalid'] = ariaInvalid;
    if (ariaRequired !== undefined) props['aria-required'] = ariaRequired;
    if (positioning !== undefined) props.positioning = positioning;
    if (inlinePopup !== undefined) props.inlinePopup = inlinePopup;
    if (size !== undefined) props.size = size;
    return props;
  }, [
    mergedListbox,
    id,
    className,
    placeholder,
    disabled,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    ariaInvalid,
    ariaRequired,
    positioning,
    inlinePopup,
    size,
  ]);

  return (
    <Combobox
      value={displayValue}
      selectedOptions={selectedOptions}
      onOptionSelect={handleOptionSelect}
      onChange={handleInput}
      onOpenChange={handleOpenChange}
      freeform
      input={{ autoComplete: 'off', onBlur: handleInputBlur }}
      {...optionalProps}
    >
      {renderedOptions}
    </Combobox>
  );
};

SearchableCombobox.displayName = 'SearchableCombobox';
